import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Activity, Users, Lock, RefreshCw,
  ShieldOff, CheckCircle, Globe, MonitorSmartphone, TrendingUp,
} from 'lucide-react';

/** Security Command Center — admin dashboard.
 *
 *  Polls /api/security/dashboard/summary + /threats every 5 seconds so the
 *  UI stays fresh without WebSockets. Heavy-lifting (risk scoring) lives
 *  entirely on the server; this page is a pure read-model.
 */
const POLL_MS = 5000;

const LEVEL_STYLES = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH:     'bg-orange-100 text-orange-800 border-orange-300',
  MEDIUM:   'bg-amber-100 text-amber-800 border-amber-300',
  LOW:      'bg-slate-100 text-slate-700 border-slate-300',
};

const SecurityDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [threats, setThreats] = useState([]);
  const [riskyUsers, setRiskyUsers] = useState([]);
  const [failedByIp, setFailedByIp] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});      // per-action loading flags
  const [minLevel, setMinLevel] = useState('MEDIUM');

  const fetchAll = useCallback(async () => {
    try {
      const [s, t, r, f] = await Promise.all([
        axiosInstance.get('/security/dashboard/summary'),
        axiosInstance.get('/security/dashboard/threats', { params: { min_level: minLevel, limit: 50 } }),
        axiosInstance.get('/security/dashboard/risky-users', { params: { limit: 10 } }),
        axiosInstance.get('/security/dashboard/failed-logins', { params: { hours: 24 } }),
      ]);
      setSummary(s.data);
      setThreats(t.data.threats || []);
      setRiskyUsers(r.data.users || []);
      setFailedByIp(f.data.rows || []);
    } catch (e) {
      if (loading) toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [minLevel, loading]);

  useEffect(() => {
    fetchAll();
    axiosInstance.get('/security/dashboard/rules').then(r => setRules(r.data)).catch(() => {});
    const iv = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const callAction = async (path, key, successMsg) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const { data } = await axiosInstance.post(path);
      toast.success(successMsg + (data.sessions_revoked != null ? ` (${data.sessions_revoked} sessions revoked)` : ''));
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading security dashboard…</div>;

  return (
    <div data-testid="security-dashboard" className="space-y-6">
      <div className="page-header">
        <h2 className="page-title flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" /> Security Command Center
        </h2>
        <button onClick={fetchAll} className="btn-secondary inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <StatCard label="Active Threats"     value={summary?.active_threats}     icon={AlertTriangle} color="text-red-600" />
        <StatCard label="Failed Logins Today" value={summary?.failed_logins_today} icon={ShieldOff} color="text-orange-600" />
        <StatCard label="Locked Accounts"    value={summary?.locked_accounts}    icon={Lock} color="text-rose-600" />
        <StatCard label="Active Sessions"    value={summary?.active_sessions}    icon={MonitorSmartphone} color="text-indigo-600" />
        <StatCard label="Logins Today"       value={summary?.logins_today}       icon={Activity} color="text-emerald-600" />
        <StatCard label="Admin Resets"       value={summary?.admin_resets_today} icon={Users} color="text-amber-600" />
        <StatCard label="CRITICAL Today"     value={summary?.critical_today}     icon={AlertTriangle} color="text-red-700" />
        <StatCard label="HIGH Today"         value={summary?.high_today}         icon={TrendingUp} color="text-orange-700" />
      </div>

      {/* Live Threat Feed */}
      <section className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" /> Live Threat Feed
          </h3>
          <select value={minLevel} onChange={(e) => setMinLevel(e.target.value)} className="form-select" data-testid="threat-level-filter">
            <option value="LOW">All (LOW+)</option>
            <option value="MEDIUM">MEDIUM+</option>
            <option value="HIGH">HIGH+</option>
            <option value="CRITICAL">CRITICAL only</option>
          </select>
        </div>
        {threats.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No threats matching this filter — system is calm.</p>
        ) : (
          <div className="space-y-2">
            {threats.map((t) => (
              <div key={t.id} data-testid={`threat-row-${t.id}`} className={`p-3 rounded-lg border ${LEVEL_STYLES[t.level] || LEVEL_STYLES.LOW}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${LEVEL_STYLES[t.level]}`}>{t.level}</span>
                      <span>Score {t.risk_score}</span>
                      <span className="text-xs font-normal opacity-70">— {t.event_type}</span>
                    </p>
                    <p className="text-sm mt-1">
                      <strong>{t.full_name || t.user_email || 'unknown'}</strong>
                      {t.user_email && t.full_name && <span className="opacity-70"> ({t.user_email})</span>}
                    </p>
                    <p className="text-xs opacity-80 mt-1">
                      <Globe className="w-3 h-3 inline mr-1" />
                      {t.ip || '—'} · {t.country || '--'} · device {t.device_id?.slice(0, 8)}…
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(t.flags || []).map((f) => (
                        <span key={f} className="px-2 py-0.5 rounded-full bg-white/70 text-xs font-mono">{f}</span>
                      ))}
                    </div>
                    {t.auto_action && (
                      <p className="text-xs mt-2 text-red-700 font-semibold">
                        ✓ Auto-action: {t.auto_action.revoked_sessions} sessions revoked, {t.auto_action.notified_admins} admin(s) notified
                      </p>
                    )}
                    <p className="text-xs opacity-60 mt-1">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  {t.user_id && (
                    <div className="flex flex-col gap-1.5">
                      <button
                        data-testid={`lock-${t.user_id}`}
                        disabled={busy['lock-' + t.user_id]}
                        onClick={() => callAction(`/security/admin/lock-user/${t.user_id}?minutes=60`, 'lock-' + t.user_id, 'User locked for 60 min')}
                        className="px-2.5 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <Lock className="w-3 h-3" /> Lock
                      </button>
                      <button
                        data-testid={`revoke-${t.user_id}`}
                        disabled={busy['revoke-' + t.user_id]}
                        onClick={() => callAction(`/security/admin/revoke-sessions/${t.user_id}`, 'revoke-' + t.user_id, 'Sessions revoked')}
                        className="px-2.5 py-1 text-xs font-semibold rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <ShieldOff className="w-3 h-3" /> Revoke
                      </button>
                      <button
                        data-testid={`safe-${t.id}`}
                        disabled={busy['dismiss-' + t.id]}
                        onClick={() => callAction(`/security/admin/dismiss-threat/${t.id}`, 'dismiss-' + t.id, 'Threat dismissed')}
                        className="px-2.5 py-1 text-xs font-semibold rounded bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <CheckCircle className="w-3 h-3" /> Ignore
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Two-column lower row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risky users */}
        <section className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Top Risky Users
          </h3>
          {riskyUsers.length === 0 ? (
            <p className="text-sm text-slate-500">No risk profiles yet.</p>
          ) : (
            <ul className="divide-y">
              {riskyUsers.map((u) => (
                <li key={u.user_id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{u.full_name || u.email || u.user_id}</p>
                    <p className="text-xs text-slate-500">
                      last {u.last_level} · {u.last_country || '--'} · {u.known_devices?.length || 0} devices
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${LEVEL_STYLES[u.last_level] || LEVEL_STYLES.LOW}`}>
                    {u.last_score}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Failed logins by IP */}
        <section className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-red-600" /> Failed Logins by IP (24h)
          </h3>
          {failedByIp.length === 0 ? (
            <p className="text-sm text-slate-500">No failed login attempts in the last 24h.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-2">IP</th><th>Country</th><th>Count</th><th>Last</th>
              </tr></thead>
              <tbody>
                {failedByIp.map((r) => (
                  <tr key={r.ip + r.country} className="border-t">
                    <td className="py-2 font-mono text-xs">{r.ip}</td>
                    <td className="text-xs">{r.country}</td>
                    <td className="font-bold text-red-700">{r.count}</td>
                    <td className="text-xs text-slate-500">{new Date(r.last_seen).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* Rules legend */}
      {rules && (
        <section className="bg-slate-50 rounded-xl border p-5">
          <h3 className="text-sm font-bold mb-3 text-slate-700">Risk Scoring Rules</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(rules.rules || {}).map(([flag, score]) => (
              <div key={flag} className="px-3 py-2 rounded bg-white border text-xs flex items-center justify-between">
                <span className="font-mono text-slate-700">{flag}</span>
                <span className="font-bold text-purple-700">+{score}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Levels — LOW: {rules.levels.LOW} · MEDIUM: {rules.levels.MEDIUM} · HIGH: {rules.levels.HIGH} · CRITICAL: {rules.levels.CRITICAL}
            {' '}· CRITICAL events auto-revoke sessions and notify admins.
          </p>
        </section>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg border p-3">
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs text-slate-500">{label}</p>
      {Icon && <Icon className={`w-4 h-4 ${color}`} />}
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value ?? 0}</p>
  </div>
);

export default SecurityDashboard;
